import React, { useEffect } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  selectCurrentOrganizationId,
  selectSearchData,
} from '../../store/organization/organization-slice-selectors';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import useServerValidationResponseEffect from '@common/hooks/useServerValidationResponseEffect';
import useValidatedInputEffect from '@common/hooks/useValidatedInputEffect';
import TextInput from '@common/components/Inputs/TextInput';
import { isHttpError } from '@common/interfaces/api/http-error';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import SelectInput from '../forms/SelectInput';
import useValidatedSelectEffect from '../../hooks/useValidatedSelectEffect';
import {
  validateRobotName,
  validateRobotSerialNumber,
} from '../../services/validation/robot-validation';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import { resetRobotsRestState } from '../../store/robots/robots-slice';
import { createRobot } from '../../store/robots/robots-actions';
import { selectUser } from '../../store/general/general-slice-selectors';
import OrganizationRole from '../../models/enums/organization-roles';
import Collapse from '@mui/material/Collapse';
import { getSearchResults } from '../../store/organization/organization-actions';
import SearchType from '../../models/enums/search';
import { resetSearchRestStatus } from '../../store/organization/organization-slice';
import { RobotIcon } from '@common/utils/icons/icons';
import { useSearchParams } from 'react-router-dom';
import { FilterKeys } from '@common/enums/query-params/filters';
import RobotVersion from '@common/interfaces/cleaning-map/robot-version';
import RobotGeneration from '@common/interfaces/cleaning-map/robot-generation';

const RobotForm: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setFilterParams] = useSearchParams();

  const ROBOT_ALREADY_EXISTS_ERROR_KEY = t('validation-error-robot-already-exists');

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const currentOrganizationId = useAppSelector((state) => selectCurrentOrganizationId(state));
  const user = useAppSelector((state) => selectUser(state));
  const searchData = useAppSelector((state) => selectSearchData(state));

  const { resetToggle, updateResetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      postError: robotsData.rest.postError,
      patchError: robotsData.rest.patchError,
      resetPostHandler: () =>
        dispatch(resetRobotsRestState({ postStatus: HttpFetchStatus.Idle, postError: null })),
      resetPatchHandler: () =>
        dispatch(resetRobotsRestState({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    });

  useEffect(() => {
    if (
      (robotsData.rest.postStatus === HttpFetchStatus.Success ||
        robotsData.rest.patchStatus === HttpFetchStatus.Success) &&
      open
    ) {
      updateResetToggle();
      onClose();
    }
  }, [onClose, open, robotsData.rest.patchStatus, robotsData.rest.postStatus, updateResetToggle]);

  const {
    value: name,
    validationResult: nameValidationResult,
    valueChangeHandler: nameChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => validateRobotName(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('name'),
    removeServerError,
    resetToggle,
  );

  const {
    value: serialNumber,
    validationResult: serialNumberValidationResult,
    valueChangeHandler: serialNumberChangeHandler,
  } = useValidatedInputEffect(
    '',
    (newValue) => validateRobotSerialNumber(newValue),
    (_) => () => {},
    getServerValidationResultOrNull('serialNumber'),
    removeServerError,
    resetToggle,
  );

  useEffect(() => {
    if (
      serialNumberValidationResult.error === ROBOT_ALREADY_EXISTS_ERROR_KEY &&
      serialNumber.length === 11
    ) {
      dispatch(getSearchResults({ search: serialNumber, fields: [SearchType.ROBOT] }));
    }

    return () => {
      dispatch(resetSearchRestStatus());
    };
  }, [ROBOT_ALREADY_EXISTS_ERROR_KEY, dispatch, serialNumber, serialNumberValidationResult.error]);

  const {
    value: model,
    validationResult: modelValidationResult,
    valueChangeHandler: modelChangeHandler,
  } = useValidatedSelectEffect(
    RobotVersion.K900,
    (_) => ({ isValid: true, error: t('form-help-text-robot-model') }),
    (_) => () => {},
    getServerValidationResultOrNull('model'),
    removeServerError,
    resetToggle,
  );

  const {
    value: robotGeneration,
    validationResult: robotGenerationValidationResult,
    valueChangeHandler: robotGenerationChangeHandler,
  } = useValidatedSelectEffect(
    RobotGeneration.GEN_0,
    (_) => ({ isValid: true, error: t('form-help-text-robot-robot-generation') }),
    (_) => () => {},
    getServerValidationResultOrNull('robotGeneration'),
    removeServerError,
    resetToggle,
  );

  const addRobotClickHandler = (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(
      createRobot({
        name,
        serialNumber,
        model: model as RobotVersion,
        robotGeneration: robotGeneration as RobotGeneration,
        organizationId: currentOrganizationId,
        userIds: [],
      }),
    );
  };

  const closeHandler = () => {
    dispatch(
      resetRobotsRestState({
        postStatus: HttpFetchStatus.Idle,
        postError: null,
      }),
    );
    updateResetToggle();
    onClose();
  };

  const existingRobotClickHandler = (robotId: string) => {
    setFilterParams((prevState) => {
      prevState.set(FilterKeys.RobotId, robotId);
      return prevState;
    });
    closeHandler();
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={closeHandler} maxWidth="sm" fullWidth>
        <form onSubmit={addRobotClickHandler}>
          <DialogTitle textAlign="center" variant="h2">
            {t('title-create-robot')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={4} mt={2}>
              {robotsData.rest.postError !== null && isHttpError(robotsData.rest.postError) && (
                <CustomAlert severity="error" header={t(robotsData.rest.postError.message)} />
              )}
              <TextInput
                value={name}
                onChange={nameChangeHandler}
                validationResult={nameValidationResult}
                label={t('input-label-robot-name')}
              />
              {user.data.organization.role === OrganizationRole.MANUFACTURER && (
                <React.Fragment>
                  <Stack>
                    <TextInput
                      value={serialNumber}
                      onChange={serialNumberChangeHandler}
                      validationResult={serialNumberValidationResult}
                      label={t('input-label-robot-serial-number')}
                    />

                    <Collapse
                      in={
                        serialNumberValidationResult.error === ROBOT_ALREADY_EXISTS_ERROR_KEY &&
                        searchData.data.length > 0
                      }
                      sx={{ mt: 1 }}
                    >
                      {searchData.data.map((result) => (
                        <Button
                          sx={(theme) => ({
                            px: 2,
                            justifyContent: 'flex-start',
                            color: theme.palette.action.active,
                          })}
                          onClick={() => existingRobotClickHandler(result.id)}
                          key={result.id}
                          size="large"
                          fullWidth
                          startIcon={<RobotIcon sx={{ mr: 2 }} />}
                        >
                          {`${t('button-view-robot')} - ${result.name}`}
                        </Button>
                      ))}
                    </Collapse>
                  </Stack>
                </React.Fragment>
              )}

              <SelectInput
                label={t('input-label-robot-model')}
                value={model}
                onChange={modelChangeHandler}
                validationResult={modelValidationResult}
              >
                {Object.values(RobotVersion).map((m) => (
                  <MenuItem key={m} value={m}>
                    {t('robot-model-' + m)}
                  </MenuItem>
                ))}
              </SelectInput>
              <SelectInput
                label={t('robotGeneration')}
                value={robotGeneration}
                onChange={robotGenerationChangeHandler}
                validationResult={robotGenerationValidationResult}
              >
                {Object.values(RobotGeneration).map((m) => (
                  <MenuItem key={m} value={m}>
                    {t('HardwareVersioningOption.robot_generation.' + m)}
                  </MenuItem>
                ))}
              </SelectInput>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button onClick={closeHandler}>{t('button-cancel')}</Button>
              <Button
                data-testid="button-add-robot"
                variant="contained"
                disabled={
                  robotsData.rest.postStatus === HttpFetchStatus.Loading ||
                  robotsData.rest.patchStatus === HttpFetchStatus.Loading
                }
                type="submit"
              >
                {robotsData.rest.postStatus === HttpFetchStatus.Loading ||
                robotsData.rest.patchStatus === HttpFetchStatus.Loading ? (
                  <CircularProgress size={24} />
                ) : (
                  t('button-add-robot')
                )}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>
      <CustomSnackbar
        open={robotsData.rest.postStatus === HttpFetchStatus.Success}
        title={t('success-creating-robot')}
        onClose={closeHandler}
      />
    </React.Fragment>
  );
};

export default RobotForm;
